import { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday as isTodayFn } from 'date-fns';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Activity, Moon, Heart, Brain, Utensils, Droplets } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { calculateLunarPhase, LUNAR_PHASE_NAMES } from '@/utils/lunarPhases';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

interface DayData {
  date: string;
  hasCheckIn: boolean;
  hasFoodLog: boolean;
  hasWhoopData: boolean;
  cycleDay?: number;
  menstrualFlow?: string;
  lunarPhase: string;
  lunarEmoji: string;
  mood?: string;
  energy?: number;
  sleep?: number;
  recovery?: number;
}

export function UnifiedCalendarView() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dayData, setDayData] = useState<Map<string, DayData>>(new Map());
  const [selectedDay, setSelectedDay] = useState<DayData | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMonthData();
  }, [currentMonth]);

  const loadMonthData = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const end = format(endOfMonth(currentMonth), 'yyyy-MM-dd');

      // Load check-ins
      const { data: checkIns } = await supabase
        .from('daily_checkins')
        .select('date, emotion, daily_feeling, hours_slept, menstrual_flow, menstrual_cycle_day')
        .eq('user_id', user.id)
        .gte('date', start)
        .lte('date', end);

      // Load food logs
      const { data: foodLogs } = await supabase
        .from('food_logs' as any)
        .select('date')
        .eq('user_id', user.id)
        .gte('date', start)
        .lte('date', end);

      // Load WHOOP recovery data
      const { data: whoopData } = await supabase
        .from('whoop_recovery_data' as any)
        .select('date, score_recovery')
        .eq('user_id', user.id)
        .gte('date', start)
        .lte('date', end);

      const dataMap = new Map<string, DayData>();
      const days = eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) });

      days.forEach(day => {
        const dateStr = format(day, 'yyyy-MM-dd');
        const checkIn = checkIns?.find((c: any) => c.date === dateStr);
        const hasFood = foodLogs?.some((f: any) => f.date === dateStr);
        const whoop = whoopData?.find((w: any) => w.date === dateStr);
        const phase = calculateLunarPhase(day);

        dataMap.set(dateStr, {
          date: dateStr,
          hasCheckIn: !!checkIn,
          hasFoodLog: !!hasFood,
          hasWhoopData: !!whoop,
          cycleDay: (checkIn as any)?.menstrual_cycle_day,
          menstrualFlow: (checkIn as any)?.menstrual_flow,
          lunarPhase: phase.phase,
          lunarEmoji: phase.name.split(' ')[0].charAt(0),
          mood: (checkIn as any)?.emotion || (checkIn as any)?.daily_feeling,
          energy: (checkIn as any)?.daily_feeling ? 7 : undefined,
          sleep: (checkIn as any)?.hours_slept,
          recovery: (whoop as any)?.score_recovery,
        });
      });

      setDayData(dataMap);
    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDayClick = (day: Date) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const data = dayData.get(dateStr);
    if (data && (data.hasCheckIn || data.hasFoodLog || data.hasWhoopData)) {
      setSelectedDay(data);
      setDialogOpen(true);
    }
  };

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthDays = eachDayOfInterval({ 
    start: startOfMonth(currentMonth), 
    end: endOfMonth(currentMonth) 
  });

  const startPadding = startOfMonth(currentMonth).getDay();

  return (
    <>
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <CalendarIcon className="h-6 w-6" />
            Unified Calendar View
          </h2>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-[200px] text-center font-medium">
              {format(currentMonth, 'MMMM yyyy')}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}
              disabled={currentMonth.getMonth() >= new Date().getMonth() && currentMonth.getFullYear() >= new Date().getFullYear()}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentMonth(new Date())}
              className="ml-2"
            >
              Today
            </Button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 mb-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-primary"></div>
            <span>Check-in</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-accent"></div>
            <span>Food logged</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-primary"></div>
            <span>WHOOP data</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-destructive"></div>
            <span>Menstrual flow</span>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2">
          {weekDays.map(day => (
            <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
              {day}
            </div>
          ))}
          
          {Array.from({ length: startPadding }).map((_, i) => (
            <div key={`padding-${i}`} className="aspect-square" />
          ))}
          
          {monthDays.map(day => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const data = dayData.get(dateStr);
            const isToday = isTodayFn(day);
            const isFuture = day > new Date();

            return (
              <button
                key={dateStr}
                onClick={() => !isFuture && handleDayClick(day)}
                disabled={isFuture}
                className={cn(
                  "aspect-square p-2 rounded-lg border transition-all relative group",
                  isToday && "ring-2 ring-primary",
                  !isFuture && (data?.hasCheckIn || data?.hasFoodLog || data?.hasWhoopData) 
                    ? "hover:bg-muted cursor-pointer" 
                    : "opacity-50",
                  isFuture && "opacity-30 cursor-not-allowed"
                )}
              >
                <div className="text-sm font-medium">{day.getDate()}</div>
                <div className="text-xs mt-1">{data?.lunarEmoji}</div>
                <div className="flex gap-1 mt-1 justify-center">
                  {data?.hasCheckIn && <div className="w-2 h-2 rounded-full bg-primary" />}
                  {data?.hasFoodLog && <div className="w-2 h-2 rounded-full bg-accent" />}
                  {data?.hasWhoopData && <div className="w-2 h-2 rounded-full bg-primary" />}
                  {data?.menstrualFlow && <div className="w-2 h-2 rounded-full bg-destructive" />}
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Day Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedDay && format(new Date(selectedDay.date), 'EEEE, MMMM d, yyyy')}
              <span className="text-2xl">{selectedDay?.lunarEmoji}</span>
            </DialogTitle>
          </DialogHeader>

          {selectedDay && (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-4">
                {selectedDay.hasCheckIn && (
                  <Card className="p-4">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Heart className="h-4 w-4" />
                      Daily Check-In
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {selectedDay.mood && (
                        <div>
                          <div className="text-xs text-muted-foreground">Mood</div>
                          <Badge variant="secondary">{selectedDay.mood}</Badge>
                        </div>
                      )}
                      {selectedDay.energy !== undefined && (
                        <div>
                          <div className="text-xs text-muted-foreground">Energy</div>
                          <div className="text-lg font-semibold">{selectedDay.energy}/10</div>
                        </div>
                      )}
                      {selectedDay.sleep !== undefined && (
                        <div>
                          <div className="text-xs text-muted-foreground">Sleep</div>
                          <div className="text-lg font-semibold">{selectedDay.sleep}h</div>
                        </div>
                      )}
                      {selectedDay.menstrualFlow && (
                        <div>
                          <div className="text-xs text-muted-foreground">Menstrual Flow</div>
                          <Badge variant="destructive">{selectedDay.menstrualFlow}</Badge>
                        </div>
                      )}
                      {selectedDay.cycleDay && (
                        <div>
                          <div className="text-xs text-muted-foreground">Cycle Day</div>
                          <div className="text-lg font-semibold">Day {selectedDay.cycleDay}</div>
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                {selectedDay.hasFoodLog && (
                  <Card className="p-4">
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <Utensils className="h-4 w-4" />
                      Food Tracked
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      You logged food on this day. View details in the Nutrition section.
                    </p>
                  </Card>
                )}

                {selectedDay.hasWhoopData && selectedDay.recovery !== undefined && (
                  <Card className="p-4">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      WHOOP Data
                    </h3>
                    <div>
                      <div className="text-xs text-muted-foreground">Recovery Score</div>
                      <div className="text-2xl font-bold text-primary">{selectedDay.recovery}%</div>
                    </div>
                  </Card>
                )}

                <Card className="p-4 bg-muted/30">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Moon className="h-4 w-4" />
                    Lunar Phase
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className="text-3xl">{selectedDay.lunarEmoji}</span>
                    <span className="text-sm capitalize">{selectedDay.lunarPhase.replace('_', ' ')}</span>
                  </div>
                </Card>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
